import { Reducer, useReducer } from 'react'

import {
  CasesListFilterInput,
  CasesListSortInput,
  CaseState,
  SortDirection
} from '@app/graphql/types'
import {
  AuditReportsQuery,
  useAuditReportsQuery
} from '@app/queries/ApprovedCustomersAsLawyer/AuditReports.queries'

export type AuditReportsItem = AuditReportsQuery['cases']['items'][number]

type State = {
  filters: CasesListFilterInput
  sorts: CasesListSortInput
  pagination: {
    take: number
    skip: number
  }
}

type Action =
  | {
      type: 'SET_FILTER'
      payload: CasesListFilterInput
    }
  | {
      type: 'SET_PAGINATION'
      payload: {
        take: number
        skip: number
      }
    }
  | {
      type: 'SET_SORT'
      payload: CasesListSortInput
    }
  | {
      type: 'RESET'
    }

export const DEFAULT_PAGINATION = {
  take: 50,
  skip: 0
}
export const DEFAULT_SORT = { latestQuestionApprovedAt: SortDirection.Asc }
export const DEFAULT_FILTER = {
  state: { in: [CaseState.Approved] },
  activeOnly: { equals: true },
  hasAppointment: { equals: false }
}

const reducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case 'SET_FILTER': {
      return {
        pagination: DEFAULT_PAGINATION,
        sorts: DEFAULT_SORT,
        filters: { ...state.filters, ...action.payload }
      }
    }

    case 'SET_PAGINATION': {
      return {
        ...state,
        pagination: action.payload
      }
    }

    case 'SET_SORT': {
      return {
        ...state,
        sorts: action.payload
      }
    }

    case 'RESET': {
      return {
        sorts: DEFAULT_SORT,
        pagination: DEFAULT_PAGINATION,
        filters: {
          ...DEFAULT_FILTER,
          lawyerId: state.filters.lawyerId
        }
      }
    }
  }
}

export type UseAuditReportsTableProps = {
  lawyerId: number
}

export const useAuditReportsTable = ({ lawyerId }: UseAuditReportsTableProps) => {
  const [query, dispatch] = useReducer(reducer, {
    sorts: DEFAULT_SORT,
    pagination: DEFAULT_PAGINATION,
    filters: {
      ...DEFAULT_FILTER,
      lawyerId: {
        equals: lawyerId
      }
    }
  })

  const { data, loading, error } = useAuditReportsQuery({
    variables: query,
    fetchPolicy: 'network-only',
    onCompleted: () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  })

  return {
    data,
    loading,
    error,
    query,
    dispatch
  }
}
